import React from 'react';
import { Modal, Box, Typography, Button, useMediaQuery, useTheme } from '@mui/material';
import { format } from 'date-fns';

// Define the type for your payment information
export interface PaymentTypes {
  cashapp?: string;
  venmo?: string;
}

// Define the props for PaymentModal
interface PaymentModalProps {
  open: boolean;
  onClose: () => void;
  paymentTypes?: PaymentTypes;       // Optional, may not always have both
  doorDateTime?: Date;              // So we can display "M/d" in the instructions
}

const PaymentModal: React.FC<PaymentModalProps> = ({
  open,
  onClose,
  paymentTypes,
  doorDateTime,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Handlers for CashApp and Venmo button clicks
  const handleCashAppClick = () => {
    if (!paymentTypes?.cashapp) return;
    const cashtag = paymentTypes.cashapp.replace('$', '');  // or keep '$'
    const url = `https://cash.app/${cashtag}`;
    window.open(url, '_blank');
  };

  const handleVenmoClick = () => {
    if (!paymentTypes?.venmo) return;
    const venmoUser = paymentTypes.venmo.replace('@', '');
    const url = `https://venmo.com/${venmoUser}`;
    window.open(url, '_blank');
  };

  // Format short date if we have a doorDateTime
  const shortDate = doorDateTime ? format(doorDateTime, 'M/d') : '';

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      BackdropProps={{
        style: { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()} // to avoid closing if user clicks inside the box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: isMobile ? '80%' : 400,
          // Use the MUI theme colors, or override with custom
          bgcolor: theme.palette.background.paper,
          // Force text to contrast well; text.primary typically is dark if theme is light,
          // or lighter if theme is dark. You can also override with a custom color if needed.
          color: theme.palette.text.secondary,
          borderRadius: 2,
          p: 3,
          textAlign: 'center',
          // A subtle box shadow for contrast
          boxShadow: theme.shadows[5],
        }}
      >
        <Typography variant="body1" sx={{ mb: 2 }}>
          Strike the Tower appreciates every fan's dedication and support.
          <br />Choose a way to pay below!
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 2 }}>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleCashAppClick}
            disabled={!paymentTypes?.cashapp}
            // Force button text to be theme-aware. Typically, MUI's contained "secondary" button
            // is high contrast, but you can ensure text is the theme's background default:
            sx={{
              color: theme.palette.background.default,
            }}
          >
            CashApp
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleVenmoClick}
            disabled={!paymentTypes?.venmo}
            sx={{
              color: theme.palette.background.default,
            }}
          >
            Venmo
          </Button>
        </Box>

        {shortDate && (
          <Typography variant="body2" fontStyle="italic">
            Include {shortDate} in the comment
          </Typography>
        )}
      </Box>
    </Modal>
  );
};

export default PaymentModal;
